import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Bell, Loader2, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';

const RegisterPage = () => {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('Tunisia');
  const [visaCenter, setVisaCenter] = useState('TLS');
  const [loading, setLoading] = useState(false);

  const countries = [
    { code: 'UK', flag: '🇬🇧' },
    { code: 'Ireland', flag: '🇮🇪' },
    { code: 'Tunisia', flag: '🇹🇳' },
    { code: 'Egypt', flag: '🇪🇬' },
    { code: 'Algeria', flag: '🇩🇿' },
    { code: 'Morocco', flag: '🇲🇦' },
    { code: 'Nigeria', flag: '🇳🇬' },
    { code: 'Thailand', flag: '🇹🇭' },
    { code: 'Iraq', flag: '🇮🇶' },
    { code: 'Cameroon', flag: '🇨🇲' },
    { code: 'Tanzania', flag: '🇹🇿' },
    { code: 'UAE', flag: '🇦🇪' },
    { code: 'Qatar', flag: '🇶🇦' },
    { code: 'China', flag: '🇨🇳' },
    { code: 'India', flag: '🇮🇳' },
  ];

  const visaCenters = [
    { code: 'TLS', name: 'TLS Contact' },
    { code: 'VFS Global', name: 'VFS Global' },
    { code: 'BLS', name: 'BLS International' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(name, email, password, country, visaCenter);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Unlimited SMS & Email Alerts',
    'Real-time Monitoring',
    'Cancel Anytime',
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      {/* Form Side */}
      <div className="flex flex-col justify-center px-4 py-12 sm:px-6 lg:px-8 bg-white">
        <div className="mx-auto w-full max-w-sm">
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0A0A0A] mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-[#FF3B30] rounded-sm flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-[#0A0A0A]">VisaAlerts</span>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="font-heading text-2xl" data-testid="register-title">
                {t('register_title')}
              </CardTitle>
              <CardDescription>
                {t('register_subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('register_name')}</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="h-11 rounded-sm"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('register_email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-sm"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('register_password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-11 rounded-sm"
                    data-testid="register-password-input"
                  />
                  <p className="text-xs text-gray-500">Minimum 6 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Residence Country</Label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="h-11 rounded-sm" data-testid="register-country-select">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className="flex items-center gap-2">
                            <span>{c.flag}</span>
                            <span>{c.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="visaCenter">Visa Centre</Label>
                  <Select value={visaCenter} onValueChange={setVisaCenter}>
                    <SelectTrigger className="h-11 rounded-sm" data-testid="register-visa-center-select">
                      <SelectValue placeholder="Select visa centre" />
                    </SelectTrigger>
                    <SelectContent>
                      {visaCenters.map((vc) => (
                        <SelectItem key={vc.code} value={vc.code}>
                          <span>{vc.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-semibold rounded-sm"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    t('register_submit')
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-600">
                {t('register_login')}{' '}
                <Link 
                  to="/login" 
                  className="font-semibold text-[#FF3B30] hover:underline"
                  data-testid="register-login-link"
                >
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Side */}
      <div className="hidden lg:block relative bg-[#0A0A0A]">
        <div className="absolute inset-0 flex flex-col justify-center px-12">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Start Getting Alerts Today
          </h2>
          <p className="text-gray-400 mb-8">
            Never miss a visa appointment slot announcement.
          </p>
          
          <div className="space-y-4">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#FF3B30] flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-white">{benefit}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-white/5 rounded-sm border border-white/10">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-2xl font-heading text-gray-400 line-through">€10</span>
              <span className="text-4xl font-heading font-bold text-white">€7.99</span>
              <span className="text-gray-400">/month</span>
            </div>
            <p className="text-gray-400 text-sm">Full access. Cancel anytime.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
